// Страницы по предмету /{ru|az|en}/tutors/{subject} (16.10.2026, SEO фаза 2).
//
// Страница есть, только если у предмета ≥ SUBJECT_PAGE_MIN опубликованных
// анкет репетиторов (не на паузе): нет анкет — нет страницы (404, как у
// неизвестного slug) и нет адреса в sitemap. Слаг — код предмета из
// справочника (design/content/map-taxonomy.json), своих слагов нет.
// Предмет × район — не делаем.
//
// Адрес общий с профилем (/tutors/$slug): у slug анкеты хвост из id
// (src/lib/partner-slug.ts), с кодом предмета он не совпадает.
//
// Без серверных импортов: файл читают маршруты, sitemap и проверка.
import { TUTOR_CATEGORIES, categoryOf, labelOf, subjectItemsFor, type Lang3 } from "@/data/map-taxonomy";

export const SUBJECT_PAGE_MIN = 3;
const SITE = "https://yolutap.com";
const TUTORS: Record<Lang3, string> = { ru: "/ru/tutors", az: "/az/tutors", en: "/en/tutors" };

/** Минимум строки анкеты для подсчёта. */
export type SubjectRow = { kind: string; subjects: string[] | null; is_paused?: boolean | null };

const ITEMS = subjectItemsFor("tutor");
export const isTutorSubject = (code: string) => ITEMS.some((i) => i.code === code);

/** Код предмета → число анкет репетиторов с ним (паузы не считаются). */
export function subjectCounts(rows: SubjectRow[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const r of rows) {
    if (r.kind !== "tutor" || r.is_paused) continue;
    for (const s of new Set(r.subjects ?? [])) if (isTutorSubject(s)) m.set(s, (m.get(s) ?? 0) + 1);
  }
  return m;
}

/** Коды предметов, у которых есть страница, — в порядке справочника. */
export function subjectPageCodes(rows: SubjectRow[]): string[] {
  const c = subjectCounts(rows);
  return ITEMS.map((i) => i.code).filter((code) => (c.get(code) ?? 0) >= SUBJECT_PAGE_MIN);
}

/** Подпись предмета без пояснения в скобках. */
export const subjectLabel = (code: string, lang: Lang3) => labelOf(ITEMS, code, lang).replace(/\s*\([^)]*\)/g, "");

export const subjectPath = (code: string, lang: Lang3) => `${TUTORS[lang]}/${code}`;

/** «математика» — строчная в русском, кроме IELTS, SAT и т. п. */
const ruLower = (s: string) => (s.length > 1 && s[1] === s[1].toLowerCase() ? s[0].toLowerCase() + s.slice(1) : s);

const COUNT_WORD: Record<Lang3, (n: number) => string> = {
  az: () => "repetitor",
  en: (n) => (n === 1 ? "tutor" : "tutors"),
  ru: (n) => {
    const a = n % 100;
    const b = n % 10;
    if (a > 10 && a < 20) return "репетиторов";
    if (b === 1) return "репетитор";
    if (b >= 2 && b <= 4) return "репетитора";
    return "репетиторов";
  },
};

/** H1, title (≤ 60) и description (≤ 160) страницы предмета. n — число анкет. */
export function subjectPageText(code: string, lang: Lang3, n: number): { h1: string; title: string; description: string; lead: string } {
  const s = subjectLabel(code, lang);
  if (lang === "az") {
    const h1 = `${s} repetitorları Azərbaycanda`;
    return {
      h1,
      title: fit(`${h1} | Yolutap`, `${s} repetitorları | Yolutap`),
      description: `${s}: Yolutap xəritəsində ${n} ${COUNT_WORD.az(n)} — rayon, metro, siniflər və dərs qiyməti. Valideynlər üçün pulsuz, əlaqə birbaşa repetitorla.`,
      lead: `Xəritədə ${n} ${COUNT_WORD.az(n)} — rayon, siniflər və qiymətlə.`,
    };
  }
  if (lang === "ru") {
    const h1 = `Репетиторы в Азербайджане: ${ruLower(s)}`;
    return {
      h1,
      title: fit(`${h1} | Yolutap`, `Репетиторы: ${ruLower(s)} | Yolutap`),
      description: `${s}: ${n} ${COUNT_WORD.ru(n)} на карте Yolutap — район, метро, классы и цена урока. Для семей бесплатно, связь с репетитором напрямую.`,
      lead: `На карте ${n} ${COUNT_WORD.ru(n)} — с районом, классами и ценой.`,
    };
  }
  const h1 = `${s} tutors in Azerbaijan`;
  return {
    h1,
    title: fit(`${h1} | Yolutap`, `${s} tutors | Yolutap`),
    description: `${n} ${s} ${COUNT_WORD.en(n)} on the Yolutap map: district, metro, grades and lesson price. Free for families, contact the tutor directly.`,
    lead: `${n} ${COUNT_WORD.en(n)} on the map — with district, grades and price.`,
  };
}

const fit = (a: string, b: string) => (a.length <= 60 ? a : b);

/**
 * Соседние предметы для перелинковки: страницы того же раздела справочника,
 * потом остальные — не больше max. Только предметы, у которых страница есть.
 */
export function neighbourSubjects(code: string, pageCodes: string[], max = 8): string[] {
  const cat = categoryOf("tutor", code);
  const same = TUTOR_CATEGORIES.find((c) => c.code === cat)?.items.map((i) => i.code) ?? [];
  const rest = pageCodes.filter((c) => c !== code);
  return [...rest.filter((c) => same.includes(c)), ...rest.filter((c) => !same.includes(c))].slice(0, max);
}

/** Минимум анкеты для ItemList. */
export type ListedPartner = { name: string; slug: string };

const HUB_NAME: Record<Lang3, string> = { az: "Repetitorlar və dərnəklər", ru: "Репетиторы и кружки", en: "Tutors and clubs" };
const HOME: Record<Lang3, string> = { az: "/az", ru: "/ru", en: "/en" };

/** JSON-LD ItemList из карточек: позиция, адрес профиля, имя. */
export function itemListJsonLd(rows: ListedPartner[], lang: Lang3, name: string) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    numberOfItems: rows.length,
    itemListElement: rows.map((r, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE}${TUTORS[lang]}/${r.slug}`, name: r.name })),
  };
}

/**
 * Предмет для крошек профиля (29.10.2026): первый предмет анкеты репетитора, у
 * которого есть страница. Кружок или ни одной страницы — null (крошка без предмета).
 */
export function profileCrumbSubject(p: { kind: string; subjects: string[] }, pageCodes: string[]): string | null {
  if (p.kind !== "tutor") return null;
  return p.subjects.find((s) => pageCodes.includes(s)) ?? null;
}

/** Предметы анкеты, у которых есть страница, — для ссылок профиля. */
export const profileSubjectPages = (p: { kind: string; subjects: string[] }, pageCodes: string[]) =>
  p.kind === "tutor" ? p.subjects.filter((s) => pageCodes.includes(s)) : [];

/** BreadcrumbList профиля: Yolutap → репетиторы → предмет (если страница есть) → имя. */
export function profileBreadcrumbJsonLd(p: { name: string; slug: string }, lang: Lang3, subject: string | null) {
  const items = [
    { name: "Yolutap", item: `${SITE}${HOME[lang]}` },
    { name: HUB_NAME[lang], item: `${SITE}${TUTORS[lang]}` },
    ...(subject ? [{ name: subjectLabel(subject, lang), item: `${SITE}${subjectPath(subject, lang)}` }] : []),
    { name: p.name, item: `${SITE}${TUTORS[lang]}/${p.slug}` },
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((x, i) => ({ "@type": "ListItem", position: i + 1, ...x })),
  };
}

/** BreadcrumbList страницы предмета: Yolutap → репетиторы → предмет. */
export function subjectBreadcrumbJsonLd(code: string, lang: Lang3) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Yolutap", item: `${SITE}${HOME[lang]}` },
      { "@type": "ListItem", position: 2, name: HUB_NAME[lang], item: `${SITE}${TUTORS[lang]}` },
      { "@type": "ListItem", position: 3, name: subjectLabel(code, lang), item: `${SITE}${subjectPath(code, lang)}` },
    ],
  };
}

